import React, {
  useImperativeHandle,
  forwardRef,
  useEffect,
  useState,
  useCallback
} from "react";
import { Row, Checkbox } from "antd";
import { WidgetType } from "@models/widget";
import { FormattedMessage } from "react-intl";
import ItemForm from "@components/widgets/ItemForm";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { WidgetItem } from "@models/widget";
import update from "immutability-helper";
import __t from '../../intl';

const SortableWidgetItem = SortableElement(props => {
  return <ItemForm {...props} />;
});

const SortableWidgetsContainer = SortableContainer(({ children }) => {
  return <Row gutter={24}>{children}</Row>;
});

type ItemsManagerStateShape = {
  items: WidgetItem[];
  type: string;
  automated: boolean;
};

/**
 * This Class manages items
 * Carousel widgest has an automated options witch disable items selection
 * The return value of this class is null or object
 * Return object has 2 state: items:[] OR automated:Boolean
 * @param props
 * @param ref
 * @constructor
 */

function ItemsManager(props: ItemsManagerStateShape, ref) {
  const initState = {
    items: props.items || [],
    automated: props.automated || false
  };
  const [items, setItems] = useState(initState.items);
  const [error, setError] = useState(false);
  const [automated, setAutomated] = useState(initState.automated);


  useEffect(() => {
    setItems(props.items);
  }, [props.items]);

  useEffect(() => {
    setAutomated(props.automated);
  }, [props.automated]);

  useImperativeHandle(ref, () => ({
    getItems: () => {
      if (automated) {
        return {
          automated,
          items: []
        };
      }

      const error = validateItemsCount();
      if(error){
        setError(error);
        return;
      }

      return {
        items: items.map((item, index) => ({
          ...item,
          order: index
        }))
      };
    }
  }));

  const validateItemsCount = () => {
    let error;

    if(items.length === 0){
      error = __t("items_is_required")
    }
    else{
      switch (props.type){
        case WidgetType.FRANCHISE:
          error = items.length !== 6 ? __t("items_count_error {count}", {count : 6}) : false;
          break

         case WidgetType.PROMOTED:
          error = items.length !== 3 ? __t("items_count_error {count}", {count : 3}) : false;
          break

        default:
          break;
      }

    }

    return error
  }

  const onAdd = widgetItem => {
    setItems([...items, widgetItem]);
  };

  const onDelete = index => {
    setItems([...items.slice(0, index), ...items.slice(index + 1)]);
  };

  const onEdit = (updatedIndex, data) => {
    setItems(items.map((editedItem, index) => index == updatedIndex ? ({
      ...editedItem,
      ...data
    }) : editedItem))
  };

  const hasAutomatedOptions = () => {
    const { type } = props;
    return (
      type === WidgetType.REQULAR_CAROUSEL ||
      type === WidgetType.PROMOTED_CAROUSEL
    );
  };

  const changeItemSelectionMode = e => {
    setAutomated(e.target.checked);
  };

  const renderCard = (card: WidgetItem, index: number) => {
    return (
      <ItemForm
        key={index}
        index={index}
        id={index}
        item={card}
        moveCard={moveCard}
        onDelete={onDelete}
        onEdit={onEdit}
        {...props}
      />
    );
  };
  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragCard = items[dragIndex];
      setItems(
        update(items, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard]
          ]
        })
      );
    },
    [items]
  );
  return (
    <div className={`widget-items-manager`} style={{ textAlign: "right" }}>
      {hasAutomatedOptions() && (
        <div className={`checkbox-wrapper`}>
          <FormattedMessage
            className={"checkbox-label"}
            id="app.automated_items_selection"
            defaultMessage={`Automated Items Selection`}
          />
          <Checkbox checked={automated} onChange={changeItemSelectionMode} />
        </div>
      )}

      {!automated && (
        <div>
          {/*{items.length > 0 && (*/}
            {/*<WidgetPreview items={items} type={props.type} />*/}
          {/*)}*/}

          <ItemForm
            type={props.type}
            onAdd={data => onAdd(data)}
            key={`add-${items.length + 1}th-items`}
          />
          <div id={`widget-items-list`}>
            {items.map((card, i) => renderCard(card, i))}
          </div>
          <div>
            <p className={`error-message`}>
              {error}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef(ItemsManager);
